import i18next from 'i18next';

import * as TimeHelpers from 'web-app/react/components/form-elements/time/time';
import * as GlobalEvent from 'web-app/services/global-event';

import type {PricingGroupOption} from '../types';
import type * as Types from './types';
import type {ApiProductCreation, ApiSessionPackageCreation} from '../api-types';
import * as Helpers from '../helpers/helpers';

const transformPricingGroupOptionsToPricingGroupFormItems = (
    pricingGroupOptions: Array<PricingGroupOption>,
): Array<Types.PricingGroupFormItem> => {
    const pricingGroupsWithoutAutomatic = pricingGroupOptions.filter(
        pricingGroupOption => !Helpers.isAutomaticPricingGroup(pricingGroupOption),
    );

    return pricingGroupsWithoutAutomatic.map(pricingGroup => ({
        id: pricingGroup.value,
        price: undefined,
        name: pricingGroup.label,
    }));
};

const transformOpeningHoursToAvailability = (openingHours: Types.ApiOpeningHours): Types.Availability | null => {
    const isDayOpen = openingHours.open;

    if (!isDayOpen) {
        return null;
    }

    const timeFrom: TimeHelpers.Time = {
        hours: openingHours.fromHour,
        minutes: openingHours.fromMinute,
    };

    const timeTo: TimeHelpers.Time = {hours: openingHours.toHour, minutes: openingHours.toMinute};

    return {
        day: transformNumberToWeekday(openingHours.day),
        from: timeFrom,
        to: timeTo,
    };
};

const transformNumberToWeekday = (day: number): Types.Weekday => {
    const weekdayArray: Array<Types.Weekday> = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ];

    return weekdayArray[day - 1];
};

const STATIC_PROPS = {
    billOnClosingDays: ['0' as const],
    fundable: true,
    billOnChildSick: true,
    billOnChildAbsent: true,
    flexibleHours: true,
    sessionType: 'package' as const,
};

const transformNewTuitionToAPISessionPackage = (
    newTuition: Types.NewTuition,
    siteId: string,
): ApiSessionPackageCreation => {
    return {
        institutionId: siteId,
        title: newTuition.title,
        pricing: transformNewTuitionPricingToAPIPricing(newTuition.pricingGroups, newTuition.frequency),
        availability: transformAvailabilityToApiAvailability(newTuition.availability),
        ...STATIC_PROPS,
        hiddenByUser: !newTuition.addToLibrary,
    };
};

const transformAvailabilityToApiAvailability = (
    availability: Array<Types.Availability>,
): ApiSessionPackageCreation['availability'] => {
    return availability.map(item => {
        return {
            day: Helpers.getDay(item.day),
            start: TimeHelpers.formatForNativeInputField(item.from),
            end: TimeHelpers.formatForNativeInputField(item.to),
        };
    });
};

const transformNewTuitionPricingToAPIPricing = (
    pricingGroups: Types.NewTuition['pricingGroups'],
    frequency: Types.NewTuition['frequency'],
): ApiSessionPackageCreation['pricing'] => {
    const frequencyToPeriodMap: Record<Types.Frequency, ApiSessionPackageCreation['pricing'][number]['period']> = {
        monthly: 'MONTHLY',
        weekly: 'WEEKLY',
    };

    const period = frequencyToPeriodMap[frequency];

    if (!period) {
        GlobalEvent.updateError(i18next.t('somethingWentWrong'));
    }

    return pricingGroups.map(pricingGroup => ({
        period,
        price: pricingGroup.price ?? 0,
        pricing: period,
        pricingGroupId: pricingGroup.id,
        volume: 1,
    }));
};

const transformNewFeePricingToAPIPricing = (
    pricingGroups: Types.NewFee['pricingGroups'],
    frequency: Types.NewFee['frequency'],
): ApiProductCreation['pricing'] => {
    const frequencyToPeriodMap: Record<Types.Frequency, ApiProductCreation['pricing'][number]['period']> = {
        monthly: 'MONTHLY',
        weekly: 'WEEKLY',
    };

    const period = frequencyToPeriodMap[frequency];

    if (!period) {
        GlobalEvent.updateError(i18next.t('somethingWentWrong'));
    }

    return pricingGroups.map(pricingGroup => ({
        adhocPrice: null,
        period,
        price: pricingGroup.price ?? 0,
        pricing: period,
        pricingGroupId: pricingGroup.id,
        volume: null,
    }));
};

const transformNewFeeToApiProduct = (newFee: Types.NewFee, siteId: string): ApiProductCreation => {
    return {
        discountable: true,
        free: null,
        billOnChildSick: true,
        billOnChildAbsent: true,
        billOnClosingDays: ['0' as const],
        institutionId: siteId,
        title: newFee.title,
        pricing: transformNewFeePricingToAPIPricing(newFee.pricingGroups, newFee.frequency),
        availability: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
        hiddenByUser: !newFee.addToLibrary,
    };
};

export {
    transformPricingGroupOptionsToPricingGroupFormItems,
    transformOpeningHoursToAvailability,
    transformNewTuitionToAPISessionPackage,
    transformNewFeeToApiProduct,
};
